/**
 * Unified auth facade across providers. Owns the shared observable state
 * and routes sign-in / token-refresh calls to the right provider.
 *
 * The provider modules (googleAuth, appleAuth) handle the provider-specific
 * SDK interactions and report results back via this facade.
 */

import { ObservableProperty, IObservableProperty } from "../observable/observableProperty";

export type ProviderName = "google" | "apple";

export interface AuthIdentity {
  playerId: string;
  provider: ProviderName;
  email?: string;
  name?: string;
}

export type AuthState =
  | { status: "loading" }
  | { status: "signedOut" }
  | { status: "signedIn"; identity: AuthIdentity; idToken: string };

/** Singleton observable state — read by UI components. */
export const authState: IObservableProperty<AuthState> = new ObservableProperty<AuthState>({
  status: "loading",
});

/** Shared localStorage keys. */
export const STORAGE_KEYS = {
  playerId: "WirderAuthPlayerIdV1",
  display: "WirderAuthDisplayV1",
  provider: "WirderAuthProviderV1",
  sessionToken: "WirderAuthSessionTokenV1",
  rosterToken: "WirderRosterTokenV1",
} as const;

function credentialKey(baseKey: string, playerId: string): string {
  return `${baseKey}:${playerId}`;
}

function currentStoredPlayerId(): string | null {
  const current = authState.value;
  return current.status === "signedIn"
    ? current.identity.playerId
    : localStorage.getItem(STORAGE_KEYS.playerId);
}

function readPlayerCredential(baseKey: string, playerId?: string): string | null {
  const owner = playerId ?? currentStoredPlayerId();
  if (!owner) return null;
  const scoped = localStorage.getItem(credentialKey(baseKey, owner));
  if (scoped) return scoped;
  if (localStorage.getItem(STORAGE_KEYS.playerId) !== owner) return null;
  const legacy = localStorage.getItem(baseKey);
  if (legacy) {
    localStorage.setItem(credentialKey(baseKey, owner), legacy);
    localStorage.removeItem(baseKey);
  }
  return legacy;
}

/**
 * Get the session JWT minted by /api/me, if any. Returned as the bearer
 * token by `getAuthToken()`. Survives webview reloads and app restarts
 * because it's in localStorage — that's the whole point of session tokens.
 */
export function getSessionToken(playerId?: string): string | null {
  return readPlayerCredential(STORAGE_KEYS.sessionToken, playerId);
}

/** Persist a session token returned from /api/me. */
export function setSessionToken(playerId: string, token: string): void {
  localStorage.setItem(credentialKey(STORAGE_KEYS.sessionToken, playerId), token);
}

/** Clear a session token, e.g. after the server rejects it as expired. */
export function clearSessionToken(playerId?: string): void {
  const owner = playerId ?? currentStoredPlayerId();
  if (owner) {
    localStorage.removeItem(credentialKey(STORAGE_KEYS.sessionToken, owner));
  }
  if (!owner || localStorage.getItem(STORAGE_KEYS.playerId) === owner) {
    localStorage.removeItem(STORAGE_KEYS.sessionToken);
  }
}

/** Read the signed menu-discovery capability without making a launch-time API call. */
export function hasOperationsAccess(): boolean {
  const token = getSessionToken();
  if (!token) return false;
  try {
    const encoded = token.split(".")[1];
    if (!encoded) return false;
    const base64 = encoded.replace(/-/g, "+").replace(/_/g, "/");
    const padded = base64.padEnd(Math.ceil(base64.length / 4) * 4, "=");
    const payload = JSON.parse(atob(padded)) as { operationsAccess?: unknown };
    return payload.operationsAccess === true;
  } catch {
    return false;
  }
}

/**
 * The secret roster token minted by /api/me on sign-in. Gates the player's own
 * friend-roster projection on the CDN. Persisted so the friend UI can load the
 * roster on app boot without re-calling /api/me.
 */
export function getRosterToken(playerId?: string): string | null {
  return readPlayerCredential(STORAGE_KEYS.rosterToken, playerId);
}

export function setRosterToken(playerId: string, token: string): void {
  localStorage.setItem(credentialKey(STORAGE_KEYS.rosterToken, playerId), token);
}

/**
 * Rehydrate auth state from localStorage on app boot. Called by index.tsx
 * before React mounts so the first render sees the right state.
 */
export function rehydrateAuth(): void {
  const cachedId = localStorage.getItem(STORAGE_KEYS.playerId);
  const cachedProvider = localStorage.getItem(STORAGE_KEYS.provider) as ProviderName | null;
  if (cachedId && (cachedProvider === "google" || cachedProvider === "apple")) {
    let display: { email?: string; name?: string } = {};
    try {
      const raw = localStorage.getItem(STORAGE_KEYS.display);
      if (raw) display = JSON.parse(raw);
    } catch {
      // ignore parse errors
    }
    authState.value = {
      status: "signedIn",
      identity: {
        playerId: cachedId,
        provider: cachedProvider,
        email: display.email,
        name: display.name,
      },
      idToken: "",
    };
  } else {
    authState.value = { status: "signedOut" };
  }
}

/** Store a successful sign-in result and update observable state. */
export function setSignedIn(identity: AuthIdentity, idToken: string): void {
  localStorage.setItem(STORAGE_KEYS.playerId, identity.playerId);
  localStorage.setItem(STORAGE_KEYS.provider, identity.provider);
  localStorage.setItem(
    STORAGE_KEYS.display,
    JSON.stringify({ email: identity.email, name: identity.name }),
  );
  authState.value = { status: "signedIn", identity, idToken };
}

/**
 * Set a fake signed-in identity for local test mode (see src/testMode.ts).
 * Deliberately does NOT touch localStorage, so it never pollutes or overrides
 * a real signed-in account on the device.
 */
export function setTestSignedIn(
  playerId = "test-player",
  name = "Test Player",
): void {
  authState.value = {
    status: "signedIn",
    identity: { playerId, provider: "google", name },
    idToken: "",
  };
}

/** Sign out completely — clears storage and resets observable state. */
export function signOut(): void {
  const playerId = currentStoredPlayerId();
  if (playerId) {
    localStorage.removeItem(credentialKey(STORAGE_KEYS.sessionToken, playerId));
    localStorage.removeItem(credentialKey(STORAGE_KEYS.rosterToken, playerId));
  }
  localStorage.removeItem(STORAGE_KEYS.playerId);
  localStorage.removeItem(STORAGE_KEYS.display);
  localStorage.removeItem(STORAGE_KEYS.provider);
  localStorage.removeItem(STORAGE_KEYS.sessionToken);
  localStorage.removeItem(STORAGE_KEYS.rosterToken);
  authState.value = { status: "signedOut" };
}

/**
 * Returns the API base URL, with a localStorage override for local dev.
 */
export function getApiUrl(): string {
  return localStorage["WirderUseLocalhostApi"] === "1"
    ? "http://localhost:7071/api"
    : __WIRDER_API_BASE_URL__;
}
